/**
 * Platform detection utilities
 * Used to determine which platform the app is running on and provide appropriate interfaces
 */

/**
 * Check if the app is running on iOS
 * @returns {boolean} True if running on iOS
 */
export const isIOS = () => {
  return (
    typeof window !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
    window.webkit && 
    window.webkit.messageHandlers
  );
};

/**
 * Check if the app is running on Android
 * @returns {boolean} True if running on Android
 */
export const isAndroid = () => {
  return (
    typeof window !== 'undefined' &&
    /Android/.test(navigator.userAgent) &&
    window.AndroidInterface
  );
};

/**
 * Check if Android interface is available, with a timeout
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<object|null>} - Android interface or null if not available
 */
export const waitForAndroidInterface = (timeout = 5000) => {
  return new Promise((resolve) => {
    if (window.AndroidInterface) {
      console.log('[DEBUG] Android interface already available');
      resolve(window.AndroidInterface);
      return;
    }

    let timeoutId;
    
    const checkInterface = () => {
      if (window.AndroidInterface) {
        console.log('[DEBUG] Android interface became available');
        clearTimeout(timeoutId);
        resolve(window.AndroidInterface);
        return;
      }
    };

    // Check every 100ms
    const intervalId = setInterval(checkInterface, 100);

    // Timeout after specified duration
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      console.log('[DEBUG] Android interface not available after timeout');
      resolve(null);
    }, timeout);
  });
};

/**
 * Check if the app is running in a web browser (not in a native app)
 * @returns {boolean} True if running in browser
 */
export const isWeb = () => {
  return !isIOS() && !isAndroid();
};

/**
 * Get the current platform
 * @returns {'ios'|'android'|'web'} The current platform
 */
export const getPlatform = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
};

/**
 * Check if running in a PWA (Progressive Web App)
 * @returns {boolean} True if running as a PWA
 */
export const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
};

/**
 * Check if running on a mobile device (based on screen size and user agent)
 * @returns {boolean} True if running on a mobile device
 */
export const isMobile = () => {
  const mobileWidth = window.innerWidth <= 768;
  const mobileAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return mobileWidth || mobileAgent;
}; 